/**
 * Time formatting helpers for upload-attribution display.
 *
 * `formatRelative` — short human relative string ("3 hours ago", "2d ago")
 * used as the visible label on dense rows. Hover-title gets the full ISO.
 * `formatAbsUtc` — the full hover title ("2026-06-26 14:32:11 UTC").
 *
 * Pure functions, no Intl deps beyond `toLocaleString` for the absolute
 * fallback; kept simple so they tree-shake into every dense row cleanly.
 * The lexportal-wide `chart-helpers` has fmt utilities but none for
 * relative time, so this is a small new primitive.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

/** Returns a short relative string for the given ISO-8601 UTC timestamp.
 *  Past timestamps only (uploads happen in the past); future-stamped
 *  inputs fall through to "just now" (defensive — shouldn't happen). */
export function formatRelative(iso: string | undefined, now?: number): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const delta = (now ?? Date.now()) - t;
  if (delta < MINUTE_MS) return "just now";
  if (delta < HOUR_MS) {
    const m = Math.floor(delta / MINUTE_MS);
    return `${m} min${m === 1 ? "" : "s"} ago`;
  }
  if (delta < DAY_MS) {
    const h = Math.floor(delta / HOUR_MS);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  if (delta < WEEK_MS) {
    const d = Math.floor(delta / DAY_MS);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  }
  // ≥7 days — switch to the calendar date (operator usually wants the
  // absolute date once the row is more than a week old; relative loses
  // utility past that point).
  return new Date(t).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Returns "YYYY-MM-DD HH:MM:SS UTC" — the hover-title companion to
 *  `formatRelative`. */
export function formatAbsUtc(iso: string | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const d = new Date(t);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss} UTC`;
}
